import React, { useContext, lazy, Suspense } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { Context } from '../Context/AuthContext';
import ChunkErrorBoundary from '../components/ChunkErrorBoundary';
import { Login } from '../pages/User/Login';
import { Users } from '../pages/User/Users';
import { AddUser } from '../pages/User/AddUser';
import { ViewUser } from '../pages/User/ViewUser';
import { EditUser } from '../pages/User/EditUser';
import Contatos from '../pages/Contatos/Index/index';
import { EditUserPassword } from '../pages/Profile/EditUserPassword';
import ViewProfile from '../pages/Profile/ViewProfile';
import { EditProfile } from '../pages/Profile/EditProfile';
import { EditProfilePassword } from '../pages/Profile/EditProfilePassword';
import EditProfileImage from '../pages/Profile/EditProfileImage';
import EditUserImage from '../pages/Profile/EditUserImage';
import { Blank } from '../pages/Blank';
import { RedefinirSenha } from '../pages/Auth/RedefinirSenha';
import OrigensConfiguracoes from '../pages/Pessoa/OrigensConfiguracoes';
import IndicacoesConfiguracoes from '../pages/Pessoa/IndicacoesConfiguracoes';
import BeneficioConfiguracoes from '../pages/Pessoa/BeneficioConfiguracoes';
import PendenciasConfiguracoes from '../pages/Pessoa/PendenciasConfiguracoes';
import AdvogadosConfiguracoes from '../pages/Pessoa/AdvogadosConfiguracoes';
import UnidadesConfiguracoes from '../pages/Unidade/UnidadesConfiguracoes';
import {
  canAccessContratosAssinadosArea,
  getStoredUserAccess,
} from '../utils/acl/admin-area';
import { hasPermissao, PERMISSOES_TAREFAS_CONTATO } from '../utils/acl/permissoes';

const Saladesituacao = lazy(() => import('../pages/Saladesituacao').then((module) => ({
  default: module.Saladesituacao,
})));

const Novidades = lazy(() => import('../pages/Novidades').then((module) => ({
  default: module.Novidades,
})));
const MeuDia = lazy(() => import('../pages/MeuDia').then((module) => ({
  default: module.MeuDia,
})));
const Dashboard = lazy(() => import('../pages/Dashboard').then((module) => ({
  default: module.Dashboard,
})));

const Ajuda = lazy(() => import('../pages/Ajuda'));
const AjudaArtigosConfiguracoes = lazy(() => import('../pages/Ajuda/ArtigosConfiguracoes'));

const Pessoas = lazy(() => import('../pages/Pessoa/Pessoas').then((module) => ({
  default: module.Pessoas,
})));

const AddPessoa = lazy(() => import('../pages/Pessoa/AddPessoa').then((module) => ({
  default: module.AddPessoa,
})));
const EditPessoa = lazy(() => import('../pages/Pessoa/EditPessoa').then((module) => ({
  default: module.EditPessoa,
})));

const AgendamentoRealizado = lazy(() => import('../pages/Pessoa/AgendamentoRealizado').then((module) => ({
  default: module.AgendamentoRealizado,
})));
const AtendimentosPresenciais = lazy(() => import('../pages/AtendimentosPresenciais/Index'));
const TarefasContato = lazy(() => import('../pages/TarefasContato/Index'));
const TarefasContatoTipos = lazy(() => import('../pages/TarefasContato/TiposConfiguracoes'));
const ViewPessoa = lazy(() => import('../pages/Pessoa/ViewPessoa').then((module) => ({
  default: module.ViewPessoa,
})));
const Atendimento = lazy(() => import('../pages/AtendimentosPresenciais/Atendimento'));

const Lembretes = lazy(() => import('../pages/AtendimentosPresenciais/Lembretes'));

const LembretePendencias = lazy(() => import('../pages/AtendimentosPresenciais/LembretePendencias'));

const LembretePresencial = lazy(() => import('../pages/AtendimentosPresenciais/LembretePresencial'));

const LembretesConfiiguracoes = lazy(() => import('../pages/AtendimentosPresenciais/LembretesConfiiguracoes'));
const VagasAtendimentoConfiguracoes = lazy(() => import('../pages/AtendimentosPresenciais/VagasAtendimentoConfiguracoes'));
const Relatorios = lazy(() => import('../pages/Admin/Relatorios/Index'));
const FechamentoDiarioContratos = lazy(() => import('../pages/Admin/Relatorios/RelatorioContratosAssinados'));
const FechamentoCallCenter = lazy(() => import('../pages/Admin/Relatorios/FechamentoCallCenter'));
const RelatorioAgendamentosPorIndicacao = lazy(() => import('../pages/Admin/Relatorios/RelatorioAgendamentosPorIndicacao'));
const RelatorioEtapas = lazy(() => import('../pages/Admin/Relatorios/RelatorioEtapas'));
const ContratosSemAtendimento = lazy(() => import('../pages/Admin/Relatorios/ContratosSemAtendimento'));
const AtendimentosDuplicados = lazy(() => import('../pages/Admin/Relatorios/AtendimentosDuplicados'));
const AgendaPericiaAvaliacao = lazy(() => import('../pages/Admin/Relatorios/AgendaPericiaAvaliacao'));
const Pagamentos = lazy(() => import('../pages/Admin/Pagamentos'));
const Permissoes = lazy(() => import('../pages/Admin/Permissoes'));
const PainelChamados = lazy(() => import('../pages/Admin/PainelChamados'));
const Chamados = lazy(() => import('../pages/Chamados'));
const TutorialSistema = lazy(() => import('../pages/TutorialSistema'));
const UsuariosOnline = lazy(() => import('../pages/Admin/UsuariosOnline'));
const UsuariosInatividade = lazy(() => import('../pages/Admin/UsuariosInatividade'));

const RelatorioEstrategico = lazy(() => import('../pages/Dashboard/ContratosAssinados').then((module) => ({
  default: module.RelatorioEstrategico,
})));

const ComoFuncionamos = lazy(() => import('../pages/Admin/ComoFuncionamos'));

/* eslint-disable react/prop-types */
function CustomRoute({
  isPrivate,
  requiresContratosArea,
  requiresPermissao,
  ...rest
}) {
  /* eslint-enable react/prop-types */
  const { authenticated } = useContext(Context);

  if (isPrivate && !authenticated) {
    return <Redirect to="/" />;
  }

  // "/view-profile" nunca exige nenhuma permissão - fallback seguro pra não criar loop
  // de redirect agora que "/pessoas" (destino antigo) também pode ser restrita por setor.
  if (requiresContratosArea && !canAccessContratosAssinadosArea(getStoredUserAccess())) {
    return <Redirect to="/view-profile" />;
  }

  if (requiresPermissao && !hasPermissao(requiresPermissao)) {
    return <Redirect to="/view-profile" />;
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Route {...rest} />;
}

export default function RoutesAdm() {
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<div>Carregando...</div>}>
        <Switch>
          <CustomRoute exact path="/" component={Login} />
          <CustomRoute exact path="/blank" component={Blank} />
          <CustomRoute exact path="/sala" component={Saladesituacao} />
          <CustomRoute exact path="/login" component={Login} />
          <CustomRoute exact path="/ajuda" component={Ajuda} isPrivate requiresPermissao="menu.ajuda" />
          <CustomRoute exact path="/ajuda/artigos" component={AjudaArtigosConfiguracoes} isPrivate requiresPermissao="ajuda.artigos.editar" />
          <Route exact path="/redefinir-senha" component={RedefinirSenha} />

          <CustomRoute exact isPrivate path="/meu-dia" component={MeuDia} />
          <CustomRoute exact isPrivate path="/novidades" component={Novidades} />
          <CustomRoute exact isPrivate requiresPermissao="menu.dashboard" path="/dashboard" component={Dashboard} />
          <CustomRoute exact isPrivate requiresPermissao="menu.chamados" path="/chamados" component={Chamados} />
          <CustomRoute exact isPrivate requiresPermissao="menu.ajuda" path="/tutorial-sistema" component={TutorialSistema} />

          <CustomRoute exact isPrivate requiresPermissao="menu.usuarios" path="/users" component={Users} />
          <CustomRoute exact isPrivate requiresPermissao="menu.usuarios" path="/add-user" component={AddUser} />
          <CustomRoute
            exact
            isPrivate
            requiresPermissao="menu.usuarios"
            path="/view-user/:id"
            // eslint-disable-next-line react/prop-types, react/jsx-props-no-spreading
            render={(props) => <ViewUser {...props} key={props.match.params.id} />}
          />
          <CustomRoute
            exact
            isPrivate
            requiresPermissao="menu.usuarios"
            path="/edit-user/:id"
            // eslint-disable-next-line react/prop-types, react/jsx-props-no-spreading
            render={(props) => <EditUser {...props} key={props.match.params.id} />}
          />

          <CustomRoute exact isPrivate requiresPermissao="menu.pessoas" path="/pessoas" component={Pessoas} />
          <CustomRoute exact isPrivate requiresPermissao="pessoa.aba.preAtendimento" path="/add-pessoa" component={AddPessoa} />
          <CustomRoute
            exact
            isPrivate
            path="/view-pessoa/:id"
            // eslint-disable-next-line react/prop-types, react/jsx-props-no-spreading
            render={(props) => <ViewPessoa {...props} key={props.match.params.id} />}
          />
          <CustomRoute
            exact
            isPrivate
            path="/edit-pessoa/:id"
            // eslint-disable-next-line react/prop-types, react/jsx-props-no-spreading
            render={(props) => <EditPessoa {...props} key={props.match.params.id} />}
          />
          <CustomRoute
            exact
            isPrivate
            path="/agendamentorealizado/:id"
            // eslint-disable-next-line react/prop-types, react/jsx-props-no-spreading
            render={(props) => <AgendamentoRealizado {...props} key={props.match.params.id} />}
          />

          <CustomRoute exact isPrivate path="/pessoas/:id/atendimento/:atendimentoId" component={Atendimento} />
          <CustomRoute
            exact
            isPrivate
            requiresPermissao="menu.usuarios"
            path="/edit-user-password/:id"
            // eslint-disable-next-line react/prop-types, react/jsx-props-no-spreading
            render={(props) => <EditUserPassword {...props} key={props.match.params.id} />}
          />
          <CustomRoute exact isPrivate path="/view-profile" component={ViewProfile} />
          <CustomRoute exact isPrivate path="/edit-profile" component={EditProfile} />
          <CustomRoute exact isPrivate path="/edit-profile-password" component={EditProfilePassword} />
          <CustomRoute exact isPrivate path="/edit-profile-image" component={EditProfileImage} />
          <CustomRoute
            exact
            isPrivate
            requiresPermissao="menu.usuarios"
            path="/edit-user-image/:id"
            // eslint-disable-next-line react/prop-types, react/jsx-props-no-spreading
            render={(props) => <EditUserImage {...props} key={props.match.params.id} />}
          />

          <CustomRoute exact isPrivate requiresPermissao="menu.atendimentos" path="/atendimentos-presenciais" component={AtendimentosPresenciais} />

          <CustomRoute exact isPrivate requiresPermissao={PERMISSOES_TAREFAS_CONTATO} path="/tarefas-contato" component={TarefasContato} />
          <CustomRoute exact isPrivate requiresPermissao="configuracoes.tiposTarefaContato" path="/tarefas-contato/tipos" component={TarefasContatoTipos} />

          <CustomRoute exact isPrivate requiresPermissao="menu.relatorios" path="/admin/relatorios" component={Relatorios} />

          <CustomRoute exact isPrivate requiresPermissao="relatorio.fechamentoDiario" path="/admin/relatorios/contratos-assinados" component={FechamentoDiarioContratos} />
          <CustomRoute exact isPrivate requiresPermissao="relatorio.fechamentoCallCenter" path="/admin/relatorios/fechamento-call-center" component={FechamentoCallCenter} />

          <CustomRoute exact isPrivate requiresPermissao="relatorio.agendamentosPorIndicacao" path="/admin/relatorios/agendamentos-por-indicacao" component={RelatorioAgendamentosPorIndicacao} />
          <CustomRoute exact isPrivate requiresPermissao="relatorio.etapas" path="/admin/relatorios/etapas" component={RelatorioEtapas} />

          <CustomRoute exact isPrivate requiresPermissao="relatorio.contratosSemAtendimento" path="/admin/relatorios/contratos-sem-atendimento" component={ContratosSemAtendimento} />
          <CustomRoute exact isPrivate requiresPermissao="relatorio.atendimentosDuplicados" path="/admin/relatorios/atendimentos-duplicados" component={AtendimentosDuplicados} />
          <CustomRoute exact isPrivate requiresPermissao="relatorio.agendaPericiaAvaliacao" path="/admin/relatorios/agenda-pericia-avaliacao" component={AgendaPericiaAvaliacao} />
          <CustomRoute exact isPrivate requiresPermissao="menu.pagamentos" path="/pagamentos" component={Pagamentos} />

          <CustomRoute exact isPrivate requiresContratosArea path="/admin/permissoes" component={Permissoes} />

          <CustomRoute exact isPrivate requiresContratosArea path="/admin/usuarios-online" component={UsuariosOnline} />

          <CustomRoute exact isPrivate requiresContratosArea path="/admin/usuarios-inatividade" component={UsuariosInatividade} />

          <CustomRoute exact isPrivate requiresPermissao="menu.contratosAssinados" path="/relatorio-estrategico" component={RelatorioEstrategico} />

          <CustomRoute exact isPrivate requiresContratosArea path="/admin/como-funcionamos" component={ComoFuncionamos} />

          <CustomRoute exact isPrivate requiresPermissao="menu.contatos" path="/Contatos" component={Contatos} />

          <CustomRoute exact isPrivate requiresPermissao="configuracoes.lembreteAtendimento" path="/atendimentos-presenciais/lembretes" component={Lembretes} />

          <CustomRoute exact isPrivate requiresPermissao="configuracoes.lembretePendencias" path="/lembrete-pendencias" component={LembretePendencias} />

          <CustomRoute exact isPrivate requiresPermissao="configuracoes.lembretePresencial" path="/atendimentos-presenciais/lembretes-presencial" component={LembretePresencial} />

          <CustomRoute exact isPrivate requiresPermissao="configuracoes.lembretesTipos" path="/atendimentos-presenciais/lembretes-configuracoes" component={LembretesConfiiguracoes} />

          <CustomRoute exact isPrivate requiresPermissao="configuracoes.vagasAtendimento" path="/atendimentos-presenciais/vagas-atendimento-configuracoes" component={VagasAtendimentoConfiguracoes} />
          <CustomRoute exact isPrivate requiresPermissao="configuracoes.painelChamados" path="/painel-chamados" component={PainelChamados} />

          <CustomRoute exact isPrivate requiresPermissao="configuracoes.origens" path="/pessoas/origens" component={OrigensConfiguracoes} />

          <CustomRoute exact isPrivate requiresPermissao="configuracoes.indicacoes" path="/pessoas/indicacoes" component={IndicacoesConfiguracoes} />

          <CustomRoute exact isPrivate requiresPermissao="configuracoes.beneficios" path="/pessoas/beneficios" component={BeneficioConfiguracoes} />

          <CustomRoute exact isPrivate requiresPermissao="configuracoes.pendencias" path="/pessoas/pendencias" component={PendenciasConfiguracoes} />

          <CustomRoute exact isPrivate requiresPermissao="configuracoes.advogados" path="/pessoas/advogados" component={AdvogadosConfiguracoes} />

          <CustomRoute exact isPrivate requiresPermissao="configuracoes.unidades" path="/unidades" component={UnidadesConfiguracoes} />

        </Switch>
      </Suspense>
    </ChunkErrorBoundary>
  );
}
